import React, { useState } from 'react';
import { Upload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import DriveSelector from './DriveSelector';

interface DriveUploaderProps {
  cuid?: string;
  fileType?: string;
  accept?: string;
  onUploadSuccess?: (url: string) => void;
  onUploadComplete?: (url: string) => void;
}

export default function DriveUploader({ cuid, fileType, accept, onUploadSuccess, onUploadComplete }: DriveUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

  const notifySuccess = (url: string) => {
    if (onUploadSuccess) onUploadSuccess(url);
    if (onUploadComplete) onUploadComplete(url);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');
    setSuccess('');

    const token = localStorage.getItem('jwt_token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      let finalUrl = '';
      if (cuid && fileType) {
        // Story media upload endpoint
        const res = await fetch(`${API_URL}/stories/${cuid}/media/${fileType}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Upload failed');
        finalUrl = `${API_URL}/stories/${cuid}/media/${fileType}?v=${Date.now()}`;
      } else {
        // Generic asset upload endpoint
        const res = await fetch(`${API_URL}/assets/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Upload failed');
        finalUrl = data.asset?.url || data.url || '';
      }

      setSuccess('Uploaded successfully!');
      notifySuccess(finalUrl);
      
    } catch (err: any) {
      setError(err.message || 'Error uploading file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="flex items-center gap-2">
      <label className="relative cursor-pointer bg-slate-100 hover:bg-slate-200 px-3 py-1 border border-slate-300 rounded text-xs font-medium text-slate-700 flex items-center gap-1 transition-colors">
        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3 text-indigo-600" />}
        <span>{uploading ? 'Uploading...' : 'Upload File'}</span>
        <input
          type="file"
          className="sr-only"
          accept={accept || 'image/*'}
          onChange={handleFileChange}
          disabled={uploading}
        />
      </label>
      
      {cuid && fileType && (
        <DriveSelector 
          cuid={cuid} 
          fileType={fileType} 
          label="Select" 
          onSelectSuccess={(url) => {
            setSuccess('Selected successfully!');
            notifySuccess(url);
          }} 
        />
      )}

      {success && <span title={success}><CheckCircle className="w-4 h-4 text-emerald-600" /></span>}
      {error && <span title={error}><AlertCircle className="w-4 h-4 text-red-600" /></span>}
    </div>
  );
}
